import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Section from '../components/layout/Section'
import Reveal from '../components/layout/Reveal'
import ListingCard from '../components/ListingCard'
import FrostOverlay from '../components/FrostOverlay'
import PressStrip from '../components/PressStrip'
import Parallax from '../components/Parallax'
import VerticalLabel from '../components/VerticalLabel'
import AgentSpotlights from '../components/AgentSpotlights'
import ProofSection from '../components/ProofSection'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { useOurListings, ourListingRibbon } from '../hooks/useOurListings'
import Seo from '../components/seo/Seo'
import HeroShowcase from '../components/HeroShowcase'
import InstagramFeed from '../components/InstagramFeed'
import PropertyFilms from '../components/PropertyFilms'
import MlsDisclaimer from '../components/listings/MlsDisclaimer'
import { realEstateAgentSchema, webSiteSchema } from '../seo/schema'

// Leaflet stays out of the home bundle — the map chunk only loads when the
// JACK LINGO LISTINGS tab is opened (item: map should just be there, loaded).
const LiveListingsMap = lazy(() => import('../components/listings/LiveListingsMap'))

// Real published reviews — PARITY_V2 Part D.1 (verbatim, complete texts re-extracted
// 2026-08-09 from the saved agent-page snapshots; never invent names or attributions)
const ALL_TESTIMONIALS = [
  {
    quote: 'Merritt was our lead agent and an absolute pro — she toured us through numerous homes across Sussex County and brought real investor insight to each option. When we landed on the right place, she zeroed in on the key details, structured a compelling offer, and moved quickly — helping us win a very favorable deal.',
    source: 'Google review',
    context: 'Jack Lingo Lewes',
    href: 'https://www.google.com/search?q=jack+lingo+lewes+reviews',
  },
  {
    quote: 'Our experience with Shawn was exceptionally good. He is extraordinarily knowledgeable of the current local market. He created an aggressive. timeline and agenda for selling the house. He identified excellent and affordable contractors who could make timely repairs. He generated a sales brochure and package including photos of the house, rooms, and local area. He rapidly organized an open house and the house sold at the open house. He interacted with us and gave excellent guidance on the price negotiations. He provided detailed advice in the legal processes for settlement. He attended the settlement in our absence and suggested reliable, quick movers for us. We were very happy with his assistance and guidance throughout the selling process. I highly recommend him and his team.',
    source: 'Zillow Review (via jacklingo.com)',
    context: 'Shawn McDonnell',
    href: 'https://www.jacklingo.com/agents/shawn-mcdonnell',
  },
  {
    quote: 'Merritt was an absolute pleasure to work with! I would leave 10 stars but the system won\'t let me. Her knowledge of the area was a great help in setting a price for our condo. Our listing on the internet was first rate and the sale was easy and not stressful. Great follow-up and attention to detail. Always got back to us promptly. Thanks for all your hard work and professionalism! Highly recommended.',
    source: 'Zillow Review (via jacklingo.com)',
    context: 'Merritt Cooch',
    href: 'https://www.jacklingo.com/agents/merritt-cooch',
  },
  {
    quote: '"WOW....I can\'t say enough about the service Shawn provided our family. We looked at multiple homes and Shawn extensively educated us on the Rehoboth Beach area. He was proactive in finding what has turned out to be the perfect beach house. He asked great questions, listened, and guided us along every step in the process. Shawn negotiated well with the sellers and exceeded our expectations. I do not hesitate to highly recommend him for any of your local real estate needs. We have found the only Delaware real estate agent we will use in the future!!! :-) Thanks again Shawn!"',
    source: 'Zillow Review (via jacklingo.com)',
    context: 'Shawn McDonnell',
    href: 'https://www.jacklingo.com/agents/shawn-mcdonnell',
  },
  {
    quote: 'We cannot say enough about Merritt Cooch as a real estate professional. She did a fantastic job helping us find the home of our dreams in Rehoboth Beach — and we are very picky! We\'ve looked for years, have worked with several different realtors and Merritt stood out head and shoulders above the rest. She is knowledgeable, responsive, and proactive.',
    source: 'Client review — jacklingo.com',
    context: 'Merritt Cooch',
    href: 'https://www.jacklingo.com/agents/merritt-cooch',
  },
  {
    quote: 'Shawn made the entire process of purchasing a home in Rehoboth Beach very straightforward. He is extremely knowledgeable about the local area and was able to provide detailed advice and answer all the questions we had. He was an absolute pleasure to work with and given the chance, I\'d rely on his expertise again in the future.',
    source: 'Zillow Review (via jacklingo.com)',
    context: 'Shawn McDonnell',
    href: 'https://www.jacklingo.com/agents/shawn-mcdonnell',
  },
  {
    quote: 'Merritt did an excellent job representing us with the purchase of a home in Bethany Beach. Merritt understood the market, offered great insight, was incredibly responsive and navigated numerous contract issues with the seller.',
    source: 'Client review — jacklingo.com',
    context: 'Merritt Cooch',
    href: 'https://www.jacklingo.com/agents/merritt-cooch',
  },
  {
    quote: 'This is the second home Shawn acted as our agent - he is thorough, professional and helpful from beginning to end, offering advice along the way. He did a video walkthrough, sending us the links as we are out of town. I highly recommend Shawn for his professionalism and expertise in the market!',
    source: 'Zillow Review (via jacklingo.com)',
    context: 'Shawn McDonnell',
    href: 'https://www.jacklingo.com/agents/shawn-mcdonnell',
  },
  {
    quote: 'Merritt greatly exceeded my expectations in every way. We just bought a home in Lewes. She is very knowledgeable about the area, the market, and was able to provide referrals for some work we wanted done on the house… You could not find a better realtor!',
    source: 'Client review — jacklingo.com',
    context: 'Merritt Cooch',
    href: 'https://www.jacklingo.com/agents/merritt-cooch',
  },
  {
    quote: 'If you are looking for an attentive, friendly, knowledgeable and diligent real estate agent - Shawn is the one to work with when looking for a home in Lewes/Rehoboth Beach. We were so pleased with his attention to detail and his complete understanding of our likes and needs. As a result of this business and real estate sense - we found our FOREVER HOME! You can\'t go wrong with Shawn!',
    source: 'Zillow Review (via jacklingo.com)',
    context: 'Shawn McDonnell',
    href: 'https://www.jacklingo.com/agents/shawn-mcdonnell',
  },
  {
    quote: 'Shawn went above & beyond for us in our search for a new home. With his knowledge & expertise in Delaware real estate SHAWN guided us & made sure our purchase was the right one for us. Steering us away from homes he knew were not a good fit for our family. All this was crucial as we are relocating from Long Island and had to trust & rely entirely on SHAWN. I highly recommend SHAWN for all your real estate needs.',
    source: 'Zillow Review (via jacklingo.com)',
    context: 'Shawn McDonnell',
    href: 'https://www.jacklingo.com/agents/shawn-mcdonnell',
  },
  {
    quote: 'Shawn is one of the best agents I have ever had the pleasure to work with! Very thorough and very focused on getting the property sold. Even in an unexpected snowstorm.. he went the 25 extra miles and bought heaters and put them in our property so the pipes would not freeze. He gets the job done... no whining, no complaining, ..just solving problems!',
    source: 'Zillow Review (via jacklingo.com)',
    context: 'Shawn McDonnell',
    href: 'https://www.jacklingo.com/agents/shawn-mcdonnell',
  },
  {
    quote: 'Merritt and the Carrie Lingo team were exceptional in helping my father sell his home in Broadkill Beach. Her patience in working with an elderly gentleman was extraordinary. She connected me with an excellent and reasonably priced local contractor to perform some necessary repairs in order to obtain our asking price for the property.',
    source: 'Zillow Review (via jacklingo.com)',
    context: 'Merritt Cooch',
    href: 'https://www.jacklingo.com/agents/merritt-cooch',
  },
  {
    quote: 'Merritt is a breath of fresh air! Most real estate agents in our area are good at unlocking doors and showing up at the settlement table to collect their check, but Merritt is COMPETELY DIFFERENT!! She is in constant contact throughout the negotiation and settlement process. She keeps track of key dates, and she makes contact to verify that key dates do not pass. She knows what she is doing, and she cares about her clients. We will definitely use Merritt again!',
    source: 'Zillow Review (via jacklingo.com)',
    context: 'Merritt Cooch',
    href: 'https://www.jacklingo.com/agents/merritt-cooch',
  },
]

const TESTIMONIALS = [ALL_TESTIMONIALS[0], ALL_TESTIMONIALS[6], ALL_TESTIMONIALS[8]]

// Verified sold history (PARITY_V2 D.3/D.5 — from agent-page snapshots 2026-08-09;
// photos staged locally from the team's own jacklingo.com listings, courtesy Bright MLS)
const SOLD_PROPERTIES = [
  {
    mls: 'DESU2074170',
    price: '$4,500,000',
    address: '40 Beach Ave',
    city: 'Rehoboth Beach, DE — Indian Beach',
    beds: '7', baths: '7.5', sqft: '4,200',
    sold: '04/2025',
    image: '/media/sold/DESU2074170-40-beach-ave.jpg',
    url: 'https://www.jacklingo.com/real-estate/rehoboth-beach/40-beach-ave-106531.html',
  },
  {
    mls: 'DESU2077958',
    price: '$3,940,000',
    address: '54 Eleanor Lee Ln E',
    city: 'Rehoboth Beach, DE — Canal Corkran',
    beds: '4', baths: '4.5', sqft: '4,760',
    sold: '07/2025',
    image: '/media/sold/DESU2077958-54-eleanor-lee.jpg',
    url: 'https://www.jacklingo.com/real-estate/rehoboth-beach/54-eleanor-lee-ln-e-111262.html',
  },
  {
    mls: 'DESU2080606',
    price: '$2,900,000',
    address: '28 Far View Rd',
    city: 'Rehoboth Beach, DE — North Shores',
    beds: '5', baths: '3.5', sqft: '2,500',
    sold: '04/2025',
    image: '/media/sold/DESU2080606-28-far-view.jpg',
    url: 'https://www.jacklingo.com/real-estate/rehoboth-beach/28-far-view-rd-105524.html',
  },
  {
    mls: 'DESU2044658',
    price: '$2,595,000',
    address: '320 Laurel St',
    city: 'Rehoboth Beach, DE — Country Club Estates',
    beds: '5', baths: '5.5', sqft: '2,310',
    sold: '01/2024',
    image: '/media/sold/DESU2044658-320-laurel.jpg',
    url: 'https://www.jacklingo.com/real-estate/rehoboth-beach/320-laurel-st-110629.html',
  },
  {
    mls: 'DESU2051980',
    price: '$2,550,000',
    address: '601 King Charles Ave',
    city: 'Rehoboth Beach, DE',
    beds: '6', baths: '4', sqft: '2,950',
    sold: '02/2024',
    image: '/media/sold/DESU2051980-601-king-charles.jpg',
    url: 'https://www.jacklingo.com/real-estate/rehoboth-beach/601-king-charles-ave-109940.html',
  },
  {
    mls: 'DESU2056064',
    price: '$1,575,000',
    address: '30823 Stallion Ln',
    city: 'Lewes, DE — Showfield',
    beds: '4', baths: '4.5', sqft: '3,500',
    sold: '06/2024',
    image: '/media/sold/DESU2056064-30823-stallion.jpg',
    url: 'https://www.jacklingo.com/real-estate/lewes/30823-stallion-ln-110051.html',
  },
]

const soldTimestamp = (value?: string) => {
  const match = value?.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  return match ? Date.UTC(Number(match[3]), Number(match[1]) - 1, Number(match[2])) : 0
}

const soldMonth = (value?: string) => {
  const match = value?.match(/^(\d{2})\/\d{2}\/(\d{4})$/)
  return match ? `${match[1]}/${match[2]}` : ''
}

const Home: React.FC = () => {
  const location = useLocation()

  // P3.7: honor `/#section` links that arrive via client-side navigation
  useEffect(() => {
    if (!location.hash) return
    document.getElementById(location.hash.slice(1))?.scrollIntoView()
  }, [location.hash])

  const [activeTab, setActiveTab] = useState<'our-listings' | 'jacklingo-listings'>('our-listings')
  const [activeTestimonial, setActiveTestimonial] = useState(0)
  const [isTestimonialPaused, setIsTestimonialPaused] = useState(false)

  // Testimonial carousel: 5s auto-advance, pause on hover, respects reduced-motion.
  // `activeTestimonial` in the deps restarts the interval whenever the quote
  // changes — so manual arrow taps reset the auto-advance timer.
  useEffect(() => {
    if (isTestimonialPaused) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const id = window.setInterval(() => {
      setActiveTestimonial((i) => (i + 1) % TESTIMONIALS.length)
    }, 5000)
    return () => window.clearInterval(id)
  }, [isTestimonialPaused, activeTestimonial])

  // 'JACK LINGO LISTINGS' tab lazy-loads the real live map (urlSync off).

  // Our Listings — REAL verified actives (PARITY_V2 D.2/D.4): live /api/our-listings once
  // the worker deploys, static snapshot /data/our-listings.json until then. No SAMPLE DATA left.
  const { listings: ourListings, stale: ourListingsStale, loading: ourListingsLoading } = useOurListings()
  const activeOurListings = ourListings.filter((listing) => listing.status !== 'Sold' && listing.status !== 'Off Market')
  const liveSoldProperties = ourListings
    .filter((listing) => listing.status === 'Sold')
    .sort((a, b) => soldTimestamp(b.soldDate) - soldTimestamp(a.soldDate))
    .map((listing) => ({
      mls: listing.mls ?? `${listing.address}-${listing.soldDate ?? 'sold'}`,
      price: listing.price,
      address: listing.address,
      city: [listing.city ? `${listing.city}, DE` : 'Delaware', listing.subdivision].filter(Boolean).join(' — '),
      beds: listing.beds === null ? '—' : String(listing.beds),
      baths: listing.baths === null ? '—' : String(listing.baths + (listing.halfBaths ?? 0) * 0.5),
      sqft: listing.sqft === null ? '—' : listing.sqft.toLocaleString('en-US'),
      sold: soldMonth(listing.soldDate),
      image: listing.photoUrl || '/media/listing-placeholder.svg',
      url: listing.detailUrl,
    }))
  const soldProperties = liveSoldProperties.length ? liveSoldProperties : SOLD_PROPERTIES

  // Featured Listings expand (desktop) / swipe rail (mobile) — Polish Plan item 12
  // Recent Sales rail — desktop prev/next arrows (touch keeps native swipe)
  const soldRailRef = useRef<HTMLDivElement>(null)
  const scrollSoldRail = (dir: 1 | -1) => {
    const rail = soldRailRef.current
    if (rail) rail.scrollBy({ left: dir * Math.round(rail.clientWidth * 0.8), behavior: 'smooth' })
  }
  // Recent Sales — desktop expand-to-grid + mouse drag-to-scroll (mice can't swipe)
  const [soldExpanded, setSoldExpanded] = useState(false)
  const soldDrag = useRef({ down: false, startX: 0, scrollLeft: 0, moved: false })
  const onSoldPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') return
    const rail = soldRailRef.current
    if (!rail) return
    soldDrag.current = { down: true, startX: e.clientX, scrollLeft: rail.scrollLeft, moved: false }
  }
  const onSoldPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const rail = soldRailRef.current
    const d = soldDrag.current
    if (!rail || !d.down) return
    const dx = e.clientX - d.startX
    if (Math.abs(dx) > 4) d.moved = true
    rail.scrollLeft = d.scrollLeft - dx
  }
  const onSoldPointerUp = () => {
    soldDrag.current.down = false
  }
  const onSoldClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    // Swallow the click that ends a drag so links don't fire mid-scroll
    if (soldDrag.current.moved) {
      e.preventDefault()
      e.stopPropagation()
      soldDrag.current.moved = false
    }
  }
  const isDesktop = useMediaQuery('(min-width:1024px)')
  const visibleOurListings =
    activeTab === 'our-listings' && isDesktop ? activeOurListings.slice(0, 5) : activeOurListings

  // Neighborhood data - Updated with PART 3.6 content
  const neighborhoods: Record<
    string,
    {
      town: string
      imageUrl: string
      imageAlt: string
      description: string
      lifestyleBullets: string[]
      linkUrl: string
      promo?: { label: string; url: string }
    }
  > = {
    'lewes': {
      town: 'Lewes',
      imageAlt: 'Lewes-Rehoboth Canal with boats and marina, Lewes, Delaware',
      imageUrl: '/media/town-lewes.jpg',
      description:
        'Known as "The First Town in the First State" (1631), Lewes pairs rich history with coastal living, canalfront dining, and Cape Henlopen.',
      lifestyleBullets: ['Cape Henlopen State Park', 'Canalfront dining', 'Junction & Breakwater Trail'],
      linkUrl: '/areas/lewes',
      promo: { label: 'Showfield — Premier New Lewes Community', url: 'https://showfieldde.com' },
    },
    'rehoboth-beach': {
      town: 'Rehoboth Beach',
      imageAlt: 'Rehoboth Beach boardwalk at Rehoboth Avenue',
      imageUrl: '/media/town-rehoboth.jpg',
      description:
        'Known as "The Nation\'s Summer Capital," Rehoboth Beach offers a vibrant boardwalk, Funland, an exciting dining scene, and Tanger tax-free shopping.',
      lifestyleBullets: ['Iconic Boardwalk', 'Funland amusement park', 'Dining & entertainment district'],
      linkUrl: '/areas/rehoboth-beach',
    },
    'dewey-beach': {
      town: 'Dewey Beach',
      imageAlt: 'WWII coastal observation tower near Dewey Beach, Delaware',
      imageUrl: '/media/town-dewey.jpg',
      description:
        'Dewey Beach is Delaware\'s bay-meets-ocean nightlife town, famous for sunsets over Rehoboth Bay and abundant water sports opportunities.',
      lifestyleBullets: ['Bay-meets-ocean nightlife', 'Sunsets over Rehoboth Bay', 'Water sports & activities'],
      linkUrl: '/areas/dewey-beach',
    },
    'bethany-beach': {
      town: 'Bethany Beach',
      imageAlt: 'Umbrellas on the shore at Bethany Beach, Delaware',
      imageUrl: '/media/town-bethany.jpg',
      description:
        'Known as "The Quiet Resorts," Bethany Beach offers a family-friendly boardwalk and dune-lined streets for a peaceful coastal experience.',
      lifestyleBullets: ['Family boardwalk', 'Dune-lined streets', 'Quiet coastal atmosphere'],
      linkUrl: '/areas/bethany-beach',
    },
    'fenwick-island': {
      town: 'Fenwick Island',
      imageAlt: 'Fenwick Island Lighthouse (1859), Delaware',
      imageUrl: '/media/town-fenwick.jpg',
      description:
        'Fenwick Island is Delaware\'s southernmost quiet resort, featuring state park beaches and the historic Fenwick Lighthouse (1859).',
      lifestyleBullets: ['Southernmost resort town', 'State park beaches', 'Fenwick Lighthouse (1859)'],
      linkUrl: '/areas/fenwick-island',
    },
    'milton': {
      town: 'Milton',
      imageAlt: 'Historic Victorian home on Federal Street, Milton, Delaware',
      imageUrl: '/media/town-milton.jpg',
      description:
        'Milton is a historic shipbuilding town on the Broadkill River, home to Dogfish Head brewery and a growing corridor for value.',
      lifestyleBullets: ['Historic shipbuilding town', 'Dogfish Head brewery', 'Growth corridor value'],
      linkUrl: '/areas/milton',
    },
  }

  return (
    <div>
      <Seo
        title="Merritt & Shawn | Coastal Delaware Real Estate"
        description="Meet Merritt Cooch and Shawn McDonnell — a husband-and-wife real estate team pairing deep Delaware roots, construction expertise, and personal guidance across the coast."
        path="/"
        ogImage="https://debeachrealestate.com/media/merritt-shawn-og.jpg"
        preloadImage="/media/hero-loop-poster.jpg"
        preloadImageWidth={1920}
        jsonLd={[realEstateAgentSchema(), webSiteSchema()]}
      />
      {/* Hero Section - Cinematic Full-Bleed */}
      <section
        id="hero"
        className="relative min-h-[100svh] w-full overflow-hidden bg-neutral-charcoal md:h-screen md:min-h-0"
        aria-label="Hero section featuring aerial views of Delaware coastal properties"
      >
        {/* Poster-first media loads a single video only after the viewer requests playback. */}
        <HeroShowcase />
        
        {/* Gradient Overlay - ogroup-inspired dark charcoal with warm neutrals */}
        <div className="pointer-events-none absolute inset-0 z-[5] bg-gradient-to-b from-primary-navy/20 via-transparent to-primary-ink/45"></div>
        
      </section>

      <AgentSpotlights />

      <PressStrip />

      <InstagramFeed />

      <ProofSection />

      <PropertyFilms />

      {/* Featured Listings Section — PART 2 #2 */}
      <Section id="listings" className="bg-neutral-charcoal">
        {/* Section Header Row */}
        <div className="mb-8 flex flex-col gap-6 md:mb-12 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <div className="flex items-center gap-4 text-white/65"><span className="h-px w-10 bg-accent-gold" aria-hidden="true" /><p className="font-sans text-[10px] font-medium uppercase tracking-[0.28em]">A considered collection</p></div>
            <Parallax axis="x" factor={0.04} max={22}>
              <h2 className="mt-7 font-serif text-[clamp(4rem,8vw,8rem)] font-normal leading-[0.84] tracking-[-0.06em] text-white">
                Featured <span className="italic text-accent-gold">properties.</span>
              </h2>
            </Parallax>
          </Reveal>
          {/* VIEW ALL + Button - Top Right: ~40px tall, sharp corners, "+" rotates 90° on hover.
              Desktop only — mobile gets a compact version under the tabs below. */}
          <Reveal delay={120}>
            <a
              href="/listings"
              className="group hidden md:inline-flex items-center h-10 px-6 bg-accent-gold text-primary-ink font-display font-medium text-[12px] tracking-[0.15em] uppercase hover:bg-[#ca6f59] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-accent-gold/50 focus:ring-offset-2 focus:ring-offset-neutral-charcoal"
              aria-label="View all listings on the map"
            >
              <span>View All on Map</span>
              <span className="ml-2 text-base leading-none transition-transform duration-300 group-hover:rotate-90" aria-hidden="true">
                +
              </span>
            </a>
          </Reveal>
        </div>
        
        {/* Tabs */}
        <div className="mb-6 flex justify-start space-x-4 overflow-x-auto scrollbar-hide md:mb-12 md:justify-center">
          <button
            onClick={() => setActiveTab('our-listings')}
            className={`inline-flex min-h-11 items-center pb-2 font-display font-medium uppercase text-[12px] tracking-[0.2em] border-b-2 transition-colors duration-300 ${
              activeTab === 'our-listings'
                ? 'text-white border-accent-gold'
                : 'text-white/70 border-transparent hover:text-white'
            }`}
            aria-pressed={activeTab === 'our-listings'}
            aria-label="Show Our Listings"
          >
            Our Listings
          </button>
          <button
            onClick={() => setActiveTab('jacklingo-listings')}
            className={`inline-flex min-h-11 items-center pb-2 font-display font-medium uppercase text-[12px] tracking-[0.2em] border-b-2 transition-colors duration-300 ${
              activeTab === 'jacklingo-listings'
                ? 'text-white border-accent-gold'
                : 'text-white/70 border-transparent hover:text-white'
            }`}
            aria-pressed={activeTab === 'jacklingo-listings'}
            aria-label="Show Jack Lingo Listings"
          >
            Jack Lingo Listings
          </button>
        </div>

        {/* Compact map CTA — mobile only, tucked directly under the tabs */}
        <div data-mobile-listing-actions className="mb-6 grid grid-cols-2 gap-2 md:hidden">
          <a
            href="/listings"
            className="inline-flex min-h-11 items-center justify-center bg-accent-gold px-2 text-center font-display text-[9px] font-medium uppercase leading-tight tracking-[0.1em] text-primary-ink"
            aria-label="View all listings on the map"
          >
            <span>View All on Map</span>
            <span className="ml-1.5 text-sm leading-none" aria-hidden="true">+</span>
          </a>
          <a
            href="https://www.jacklingo.com/agents/shawn-mcdonnell"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-11 items-center justify-center border border-accent-gold bg-primary-ink px-2 text-center font-display text-[9px] font-medium uppercase leading-tight tracking-[0.1em] text-white"
            aria-label="View all Merritt and Shawn listings on Jack Lingo"
          >
            <span>View All on Jack Lingo</span>
            <span className="ml-1.5 text-sm leading-none text-accent-gold" aria-hidden="true">↗</span>
          </a>
        </div>
        
        {/* OUR LISTINGS feed status note */}
        {activeTab === 'our-listings' && ourListingsStale && !ourListingsLoading && (
          <p className="mb-6 text-center font-display text-[12px] uppercase tracking-[0.2em] text-white/60">
            Merritt & Shawn at Jack Lingo REALTOR®
          </p>
        )}

        {/* Grid (desktop): featured row = 2 large cards, then rows of 3.
            Mobile: snap carousel rail of the same cards (item 12). */}
        <div className="flex md:grid md:grid-cols-2 lg:grid-cols-6 gap-4 md:gap-8 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0 pb-4 md:pb-0">
          {activeTab === 'our-listings'
            ? ourListingsLoading
              ? [0, 1, 2].map((i) => (
                  <div key={i} className={`snap-center shrink-0 w-[85vw] md:w-auto ${i < 2 ? 'lg:col-span-3' : 'lg:col-span-2'}`}>
                    <div className="aspect-[4/3] rounded-[14px] bg-white/5 animate-pulse" aria-hidden="true" />
                  </div>
                ))
              : visibleOurListings.map((listing, index) => {
                const ribbon = ourListingRibbon(listing)
                return (
                <div key={listing.mls ?? listing.address} className={`snap-center shrink-0 w-[85vw] md:w-auto ${index < 2 ? 'lg:col-span-3' : 'lg:col-span-2'}`}>
                  <Reveal delay={(index % 3) * 120} className="h-full">
                    <ListingCard
                      title={listing.address}
                      price={listing.price}
                      address={listing.subdivision ? `${listing.address} — ${listing.subdivision}` : listing.address}
                      beds={listing.beds ?? 0}
                      baths={listing.baths ?? 0}
                      sqft={listing.sqft ?? 0}
                      imageUrl={listing.photoUrl}
                      imageAlt={listing.address}
                      linkText="View on jacklingo.com"
                      linkUrl={listing.detailUrl}
                      attribution="Merritt & Shawn at Jack Lingo REALTOR® · Bright MLS"
                      ribbon={ribbon?.label}
                      ribbonTone={ribbon?.tone}
                    />
                  </Reveal>
                </div>
                )
              })
            : (
              /* JACK LINGO LISTINGS tab = the real live map, loaded on demand.
                 urlSync={false} keeps map lat/lng/z params off the home URL.
                 The verbatim Bright MLS disclaimer lives in a tiny hover/tap
                 popover (compact) so it doesn't dominate the embed. */
              <div className="w-full md:w-auto snap-center shrink-0 lg:col-span-6">
                <Suspense
                  fallback={
                    <div className="flex h-[60vh] items-center justify-center rounded-[14px] border border-white/10 bg-primary-ink">
                      <p className="font-display text-[12px] uppercase tracking-[0.3em] text-white/50 animate-pulse">
                        Loading the map…
                      </p>
                    </div>
                  }
                >
                  <LiveListingsMap urlSync={false} />
                </Suspense>
                <MlsDisclaimer compact />
              </div>
            )}

          {/* End-of-rail map slide — mobile only, reads as organic rail end (item 12.4) */}
          {activeTab === 'our-listings' && !ourListingsLoading && activeOurListings.length > 0 && (
            <a
              href="/listings"
              className="md:hidden snap-center shrink-0 w-[60vw] flex flex-col items-center justify-center gap-3 rounded-[14px] bg-primary-ink border border-white/10"
              aria-label="Browse all listings on the map"
            >
              <img
                src="/media/team/merritt-shawn-profile.jpg"
                alt=""
                aria-hidden="true"
                width={320}
                height={320}
                className="h-12 w-12 rounded-full border-2 border-white/70 object-cover"
              />
              <span className="px-4 text-center font-display text-[12px] font-medium uppercase tracking-[0.2em] text-accent-gold">
                Browse All on the Map +
              </span>
            </a>
          )}
        </div>

        {/* Desktop expand/collapse hairline button (item 12.2) */}
        {activeTab === 'our-listings' && isDesktop && activeOurListings.length > 5 && (
          <a
            href="https://www.jacklingo.com/agents/shawn-mcdonnell"
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-10 w-full border-t border-white/15 pt-6 hidden lg:flex items-center justify-center gap-3 font-display text-[12px] tracking-[0.25em] uppercase text-white/60 hover:text-accent-gold transition-colors"
          >
            <span>View All Listings</span>
            <span className="transition-transform duration-300 group-hover:rotate-90" aria-hidden="true">+</span>
          </a>
        )}
      </Section>

      <section id="neighborhoods" className="relative overflow-hidden bg-primary-ink py-24 text-white md:py-36">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="grid gap-10 lg:grid-cols-[0.7fr_0.3fr] lg:items-end lg:gap-16">
              <div>
                <div className="flex items-center gap-4 text-primary-mist"><span className="h-px w-10 bg-accent-gold" aria-hidden="true" /><p className="font-sans text-[10px] font-medium uppercase tracking-[0.28em]">The Delaware beaches</p></div>
                <h2 className="mt-7 font-serif text-[clamp(4rem,8vw,8.5rem)] font-normal leading-[0.84] tracking-[-0.06em]">Six towns.<br /><span className="italic text-accent-gold">One coast.</span></h2>
              </div>
              <p className="max-w-md text-base font-light leading-[1.9] text-white/60">From historic Lewes to the quiet resorts, every town has its own rhythm. Explore the places Merritt and Shawn know by block, season, and shoreline.</p>
            </div>
          </Reveal>
        </div>

        <div className="mx-auto mt-16 max-w-[1500px] md:px-6">
          <div className="grid gap-px bg-white/15 lg:grid-cols-12 lg:auto-rows-[310px]">
            {Object.entries(neighborhoods).map(([town, neighborhood], index) => (
              <Reveal key={town} className={`${index === 0 ? 'lg:col-span-7 lg:row-span-2' : index < 3 ? 'lg:col-span-5' : 'lg:col-span-4'}`} delay={(index % 3) * 80}>
                <a
                  id={`neighborhood-${neighborhood.town.toLowerCase().replace(/\s+/g, '-')}`}
                  href={neighborhood.linkUrl}
                  className={`group relative block min-h-[390px] overflow-hidden bg-primary-ink lg:h-full lg:min-h-0`}
                >
                  <img src={neighborhood.imageUrl} alt={neighborhood.imageAlt} width={1600} height={1067} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.035] motion-reduce:transition-none motion-reduce:group-hover:scale-100" />
                  <span className="absolute inset-0 bg-gradient-to-t from-primary-ink/90 via-primary-ink/10 to-transparent transition-colors group-hover:from-primary-ink/75" />
                  <span className="absolute inset-x-0 bottom-0 p-7 md:p-9">
                    <span className="block font-sans text-[9px] font-medium uppercase tracking-[0.22em] text-white/55">0{index + 1} · Coastal Delaware</span>
                    <span className={`mt-3 block font-serif leading-[0.9] tracking-[-0.04em] ${index === 0 ? 'text-5xl md:text-7xl' : 'text-4xl md:text-5xl'}`}>{neighborhood.town}</span>
                    {index === 0 && <span className="mt-5 block max-w-lg text-sm font-light leading-[1.8] text-white/75">{neighborhood.description}</span>}
                    <span className="mt-5 inline-flex items-center border-b border-white/35 pb-1 font-sans text-[9px] font-medium uppercase tracking-[0.18em] transition-colors group-hover:border-accent-gold group-hover:text-accent-gold">Explore the town +</span>
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal>
          <div className="mx-auto mt-12 flex max-w-7xl flex-col gap-5 px-6 md:flex-row md:items-center md:justify-between">
            <p className="font-serif text-2xl italic text-white/70">Find the place that feels like yours.</p>
            <div className="flex flex-wrap gap-4">
              <a href="/areas" className="inline-flex h-12 items-center border border-white/30 px-6 font-sans text-[10px] font-medium uppercase tracking-[0.18em] transition-colors hover:border-accent-gold hover:text-accent-gold">View all areas</a>
              <a href="/listings" data-touch-accent className="inline-flex h-12 items-center bg-accent-gold px-6 font-sans text-[10px] font-medium uppercase tracking-[0.18em] text-primary-ink transition-colors hover:bg-white">Search the coast</a>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Testimonials Section - ogroup-inspired OG black layout with vertical label */}
      <Section id="testimonials" className="bg-primary-ink relative overflow-hidden">
        {/* Vertical sideways label on far right — writing-mode layout, counter-scroll drift (A.3) */}
        <VerticalLabel thin="WHAT CLIENTS " bold="ARE SAYING" />

        <div className="relative z-10">
          {/* Vertical label carries the section identity — keep h2 for screen readers only */}
          <h2 className="sr-only">What clients are saying</h2>

          {/* Carousel — quote left, B&W portrait right, thin ‹ › arrows, 5s auto-advance, pause on hover */}
          <div
            className="relative max-w-7xl mx-auto px-6 lg:pr-20"
            onMouseEnter={() => setIsTestimonialPaused(true)}
            onMouseLeave={() => setIsTestimonialPaused(false)}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              {/* Left: quote */}
              <Reveal className="lg:col-span-7">
                {/* All quotes stacked in one grid cell — section height stays fixed at the
                    tallest quote so the 5s auto-advance never reflows the page (no jump). */}
                <div aria-live="polite" className="grid">
                  {TESTIMONIALS.map((t, i) => (
                    <div
                      key={t.quote}
                      aria-hidden={i !== activeTestimonial}
                      className={`col-start-1 row-start-1 transition-opacity duration-700 ${
                        i === activeTestimonial ? 'opacity-100' : 'opacity-0 pointer-events-none invisible'
                      }`}
                    >
                      {/* 60px oversized ghost quotation mark */}
                      <span
                        className="block font-display text-[60px] leading-[0.5] text-accent-gold/30 select-none"
                        aria-hidden="true"
                      >
                        &ldquo;
                      </span>
                      <blockquote className="mt-8 text-sm font-light text-white/85 leading-[1.9] tracking-wide">
                        {t.quote}
                      </blockquote>
                      {/* Thin accent underline */}
                      <div className="w-[28px] h-[2px] bg-accent-gold my-6" aria-hidden="true" />
                      <p className="font-display uppercase text-[12px] tracking-[0.2em] text-white mb-1">
                        {t.source}
                      </p>
                      <p className="text-xs text-accent-gold/80 mb-8">
                        {t.context}
                      </p>
                      <a
                        href={t.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        tabIndex={i === activeTestimonial ? 0 : -1}
                        className="group inline-flex items-center h-11 px-6 bg-accent-gold text-primary-ink font-display font-medium text-[12px] tracking-[0.15em] uppercase hover:bg-[#ca6f59] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-accent-gold/50 focus:ring-offset-2 focus:ring-offset-primary-ink"
                        aria-label="Read more reviews"
                      >
                        <span>Read More</span>
                        <span className="ml-2 text-base leading-none transition-transform duration-300 group-hover:rotate-90" aria-hidden="true">
                          +
                        </span>
                      </a>
                    </div>
                  ))}
                </div>

                {/* Mobile arrows — directly under the quote (the desktop pair
                    below the grid renders after the portraits on mobile). */}
                <div className="flex lg:hidden items-center gap-6 mt-8">
                  <button
                    type="button"
                    onClick={() => setActiveTestimonial((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
                    className="p-2 text-white/60 hover:text-accent-gold transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-accent-gold/50"
                    aria-label="Previous testimonial"
                  >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTestimonial((i) => (i + 1) % TESTIMONIALS.length)}
                    className="p-2 text-white/60 hover:text-accent-gold transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-accent-gold/50"
                    aria-label="Next testimonial"
                  >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </Reveal>

              <Reveal delay={120} className="lg:col-span-5">
                <div className="group relative aspect-[4/5] max-h-[70vh] overflow-hidden">
                  <img
                    src="/media/team/merritt-shawn-coastal-portrait.webp"
                    alt="Merritt Cooch and Shawn McDonnell at Rehoboth Beach"
                    width={1000}
                    height={1250}
                    className="h-full w-full object-cover grayscale transition-all duration-[1200ms] group-hover:scale-[1.025] group-hover:grayscale-0 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-ink/45 via-transparent to-transparent" />
                  <p className="absolute bottom-6 left-6 font-sans text-[9px] font-medium uppercase tracking-[0.22em] text-white/75">Local knowledge · Personal counsel</p>
                </div>
              </Reveal>
            </div>

            {/* Thin-line arrows, no dots — desktop only (mobile pair sits under the quote) */}
            <div className="hidden lg:flex items-center gap-6 mt-10">
              <button
                type="button"
                onClick={() => setActiveTestimonial((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
                className="p-2 text-white/60 hover:text-accent-gold transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-accent-gold/50"
                aria-label="Previous testimonial"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => setActiveTestimonial((i) => (i + 1) % TESTIMONIALS.length)}
                className="p-2 text-white/60 hover:text-accent-gold transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-accent-gold/50"
                aria-label="Next testimonial"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </Section>

      {/* Sold Highlights Section - horizontal scroll strip of sold cards w/ "SOLD" gold tag */}
      <Section id="sold-highlights" className="bg-sand relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-0 md:py-24">
          {/* Section Header */}
          <Reveal>
          <div className="mb-12 md:mb-16">
            <div className="flex items-center gap-4 text-primary-ink"><span className="h-px w-10 bg-accent-gold" aria-hidden="true" /><p className="font-sans text-[10px] font-medium uppercase tracking-[0.28em]">Selected sold properties</p></div>
            <Parallax axis="x" factor={0.04} max={22}>
              <h2 className="mt-7 font-serif text-[clamp(3.8rem,7vw,7rem)] font-normal leading-[0.86] tracking-[-0.055em] text-primary-ink">Recent <span className="italic text-primary-navy">sales.</span></h2>
            </Parallax>
          </div>
          </Reveal>

          {/* Horizontal Scroll Strip — VERIFIED sold history (PARITY_V2 D.3/D.5, 2026-08-09) */}
          <div className="relative">
            {/* Desktop prev/next arrows — scrollbar is hidden, these are the scroll affordance */}
            {!(soldExpanded && isDesktop) && (
              <>
                <button
                  type="button"
                  onClick={() => scrollSoldRail(-1)}
                  aria-label="Scroll recent sales left"
                  className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 z-20 h-11 w-11 items-center justify-center rounded-full bg-primary-ink text-white shadow-lg transition-colors hover:bg-accent-gold hover:text-primary-ink"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => scrollSoldRail(1)}
                  aria-label="Scroll recent sales right"
                  className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 z-20 h-11 w-11 items-center justify-center rounded-full bg-primary-ink text-white shadow-lg transition-colors hover:bg-accent-gold hover:text-primary-ink"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
          <div
            ref={soldRailRef}
            onPointerDown={onSoldPointerDown}
            onPointerMove={onSoldPointerMove}
            onPointerUp={onSoldPointerUp}
            onPointerLeave={onSoldPointerUp}
            onClickCapture={onSoldClickCapture}
            onDragStart={(e) => e.preventDefault()}
            className={
              soldExpanded && isDesktop
                ? 'pb-8'
                : 'overflow-x-auto pb-8 -mx-6 px-6 md:mx-0 md:px-0 scrollbar-hide select-none md:cursor-grab md:active:cursor-grabbing'
            }
          >
            <div
              className={
                soldExpanded && isDesktop
                  ? 'grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8'
                  : 'flex gap-6 md:gap-8 min-w-max'
              }
            >
              {soldProperties.map((p) => (
                <article
                  key={p.mls}
                  className={`group relative overflow-hidden bg-primary-ink ${
                    soldExpanded && isDesktop ? 'w-full' : 'flex-shrink-0 w-[75vw] max-w-[300px] md:w-[320px] md:max-w-none'
                  }`}
                  aria-label={`Sold Property: ${p.address}, ${p.city}`}
                >
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block focus:outline-none focus:ring-2 focus:ring-accent-gold/50"
                    aria-label={`View ${p.address} on jacklingo.com`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={p.image}
                        alt={`${p.address}, ${p.city}`}
                        className="w-full h-full object-cover transition-transform duration-[1000ms] ease-out group-hover:scale-[1.035] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                        loading="lazy"
                      />

                      {/* Dark Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-primary-navy/90 via-primary-navy/70 to-transparent opacity-90"></div>

                      {/* "SOLD" Gold Tag - Top-Right */}
                      <div className="absolute top-4 right-4">
                        <span className="bg-accent-gold text-primary-ink px-3 py-1 text-[10px] md:text-[11px] font-display font-bold tracking-widest uppercase">
                          SOLD {p.sold}
                        </span>
                      </div>

                      {/* Frosted brand hover overlay (A.4) */}
                      <FrostOverlay cta="View Sale" />

                      {/* Price - Bottom-Left */}
                      <div className="absolute bottom-0 left-0 p-6">
                        <p className="text-[20px] md:text-[24px] font-bold text-white mb-2">
                          {p.price}
                        </p>

                        {/* 28px×2px Accent Underline Bar */}
                        <div className="w-[28px] h-[2px] bg-accent-gold mb-3"></div>

                        {/* Address - Uppercase Condensed */}
                        <p className="text-[12px] md:text-[13px] font-bold tracking-[0.2em] uppercase text-white/90 leading-tight">
                          {p.address}
                        </p>
                        <p className="text-[11px] md:text-[12px] font-sans text-white/70 mt-1">
                          {p.city}
                        </p>

                        {/* Specs on Hover */}
                        <div className="mt-4 pt-4 border-t border-white/20 flex items-center gap-3 text-[10px] md:text-[11px] font-bold tracking-widest uppercase text-white/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <span>{p.beds} Beds</span>
                          <span className="text-white/40">/</span>
                          <span>{p.baths} Baths</span>
                          <span className="text-white/40">/</span>
                          <span>{p.sqft} sqft</span>
                        </div>

                        {/* Courtesy microtext */}
                        <p className="mt-2 text-[9px] font-sans uppercase tracking-[0.15em] text-white/50">
                          Courtesy of Jack Lingo - Lewes · Bright MLS
                        </p>
                      </div>
                    </div>
                  </a>
                </article>
              ))}
            </div>
          </div>
          </div>

          {/* Desktop expand-to-grid toggle — the whole sold history at once */}
          <button
            type="button"
            onClick={() => setSoldExpanded((v) => !v)}
            aria-expanded={soldExpanded}
            className="group hidden lg:flex mt-2 w-full items-center justify-center gap-3 border-t border-primary-ink/15 pt-5 font-display text-[12px] font-medium uppercase tracking-[0.25em] text-primary-ink/60 transition-colors hover:text-accent-gold"
          >
            <span>{soldExpanded ? 'Collapse Sold History' : `Expand All ${soldProperties.length} Recent Sales`}</span>
            <span
              className={`text-base leading-none transition-transform duration-300 ${soldExpanded ? 'rotate-45' : 'group-hover:rotate-90'}`}
              aria-hidden="true"
            >
              +
            </span>
          </button>

          {/* View All Button */}
          <div className="mt-12 text-center">
            <a
              href="https://www.jacklingo.com/agents/shawn-mcdonnell"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center h-11 px-6 bg-accent-gold text-primary-ink font-display font-medium text-[12px] tracking-[0.15em] uppercase hover:bg-[#ca6f59] transition-colors duration-300"
            >
              <span>View All Sold Properties</span>
              <span className="ml-2 text-base leading-none transition-transform duration-300 group-hover:rotate-90" aria-hidden="true">
                +
              </span>
            </a>
          </div>
        </div>
      </Section>

      <section id="community" className="relative overflow-hidden bg-primary-mist py-24 text-primary-ink md:py-36">
        <VerticalLabel thin="ROOTED " bold="HERE" />
        <div className="coastal-orbit pointer-events-none absolute -left-48 top-1/4 h-[38rem] w-[38rem] rounded-full border border-primary-navy/15 motion-reduce:animate-none" aria-hidden="true" />

        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="grid gap-10 lg:grid-cols-[0.68fr_0.32fr] lg:items-end lg:gap-16">
              <div>
                <div className="flex items-center gap-4 text-primary-ink"><span className="h-px w-10 bg-accent-gold" aria-hidden="true" /><p className="font-sans text-[10px] font-medium uppercase tracking-[0.28em]">Community &amp; philanthropy</p></div>
                <Parallax axis="x" factor={0.05} max={28}>
                  <h2 className="mt-7 font-serif text-[clamp(4rem,8vw,8.5rem)] font-normal leading-[0.84] tracking-[-0.06em]">Rooted here.<br /><span className="italic text-primary-navy">Giving back here.</span></h2>
                </Parallax>
              </div>
              <p className="max-w-md border-l border-primary-ink/20 pl-7 text-base font-light leading-[1.9] text-primary-ink/65">The coast is not a market Merritt and Shawn visit. It is the community they protect, serve, and call home.</p>
            </div>
          </Reveal>

          <div className="mt-20 space-y-px bg-primary-ink/15">
            <Reveal>
              <article className="grid overflow-hidden bg-sand lg:grid-cols-[0.6fr_0.4fr]">
                <div className="relative aspect-[16/10] overflow-hidden md:aspect-auto md:min-h-[620px]">
                  <Parallax factor={0.05} max={36} className="absolute -inset-y-[7%] inset-x-0">
                    <img src="/media/fort-miles.jpg" alt="Fort Miles Historical Area at Cape Henlopen State Park" width={1600} height={1067} loading="lazy" className="h-full w-full object-cover" />
                  </Parallax>
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-ink/55 via-transparent to-transparent" />
                  <p className="absolute bottom-7 left-7 font-sans text-[9px] font-medium uppercase tracking-[0.22em] text-white">Fort Miles · Cape Henlopen</p>
                </div>
                <div className="relative flex flex-col justify-center overflow-hidden p-8 md:p-14 lg:p-16">
                  <span className="absolute -right-5 top-3 font-serif text-[10rem] italic leading-none text-primary-ink/[0.05]" aria-hidden="true">01</span>
                  <p className="relative font-sans text-[9px] font-medium uppercase tracking-[0.22em] text-primary-ink">Merritt · Historic preservation</p>
                  <h3 className="relative mt-5 font-serif text-4xl font-normal leading-tight tracking-[-0.04em] md:text-6xl">Protecting the stories that made Delaware.</h3>
                  <p className="relative mt-7 text-base font-light leading-[1.85] text-primary-ink/70">Merritt serves on the Fort Miles Museum and Cooch&apos;s Bridge historic preservation boards, helping safeguard the places and history that shape the First State.</p>
                  <div className="relative mt-9 h-px w-20 bg-accent-gold" aria-hidden="true" />
                </div>
              </article>
            </Reveal>
            <Reveal>
              <article className="grid overflow-hidden bg-primary-ink text-white lg:grid-cols-[0.4fr_0.6fr]">
                <div className="relative flex flex-col justify-center overflow-hidden p-8 md:p-14 lg:p-16">
                  <span className="absolute -left-5 top-3 font-serif text-[10rem] italic leading-none text-white/[0.04]" aria-hidden="true">02</span>
                  <p className="relative font-sans text-[9px] font-medium uppercase tracking-[0.22em] text-primary-mist">Shawn · Animal welfare</p>
                  <h3 className="relative mt-5 font-serif text-4xl font-normal leading-tight tracking-[-0.04em] md:text-6xl">A coast that cares for every kind of neighbor.</h3>
                  <p className="relative mt-7 text-base font-light leading-[1.85] text-white/65">Shawn volunteers with local animal welfare efforts and hopes to establish a Southern Delaware sanctuary, inspired by his four rescue dogs—Winnie, Hunny, Buca, and Bear.</p>
                  <div className="relative mt-9 h-px w-20 bg-accent-gold" aria-hidden="true" />
                </div>
                <div className="relative aspect-[4/3] overflow-hidden md:aspect-auto md:min-h-[620px] lg:order-last">
                  <Parallax factor={-0.05} max={36} className="absolute -inset-y-[7%] inset-x-0">
                    <img src="/media/community-dogs.jpg" alt="Yellow Labrador Retriever representing Shawn's local animal welfare work" width={1600} height={1067} loading="lazy" className="h-full w-full object-cover" />
                  </Parallax>
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-ink/55 via-transparent to-transparent" />
                  <p className="absolute bottom-7 right-7 font-sans text-[9px] font-medium uppercase tracking-[0.22em] text-white">Winnie · Hunny · Buca · Bear</p>
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="contact" className="grid overflow-hidden bg-primary-ink text-white lg:grid-cols-[0.58fr_0.42fr]">
        <div className="flex items-center px-6 py-24 md:px-[max(3rem,calc((100vw-80rem)/2))] md:py-36 lg:pr-16">
          <Reveal>
            <div className="flex items-center gap-4 text-primary-mist"><span className="h-px w-10 bg-accent-gold" aria-hidden="true" /><p className="font-sans text-[10px] font-medium uppercase tracking-[0.28em]">Buy or sell at the Delaware beaches</p></div>
            <h2 className="mt-8 font-serif text-[clamp(4rem,8vw,8rem)] font-normal leading-[0.84] tracking-[-0.06em]">Ready to make<br /><span className="italic text-accent-gold">your move?</span></h2>
            <p className="mt-8 max-w-xl text-base font-light leading-[1.9] text-white/65 md:text-lg">Whether you are buying your place at the beach or selling a home you love, Merritt and Shawn bring local context, direct guidance, and a clear point of view to the next move.</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="/contact?subject=BUYING" data-touch-accent className="inline-flex h-12 items-center bg-accent-gold px-7 font-sans text-[10px] font-medium uppercase tracking-[0.18em] text-primary-ink transition-colors hover:bg-white">Buy with us</a>
              <a href="/contact?subject=SELLING" className="inline-flex h-12 items-center border border-white/30 px-7 font-sans text-[10px] font-medium uppercase tracking-[0.18em] text-white transition-colors hover:border-accent-gold hover:text-accent-gold">Sell with us</a>
            </div>
          </Reveal>
        </div>
        <div className="relative min-h-[460px] lg:min-h-[760px]">
          <img src="/media/footer-lewes-beach.jpg" alt="Lewes Beach at the Delaware coast" width={1600} height={1067} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-ink/60 via-transparent to-transparent lg:bg-gradient-to-r lg:from-primary-ink/35 lg:to-transparent" />
          <p className="absolute bottom-8 right-8 [writing-mode:vertical-rl] font-sans text-[9px] font-medium uppercase tracking-[0.26em] text-white/75">Lewes · Rehoboth · The Delaware Beaches</p>
        </div>
      </section>
    </div>
  )
}

export default Home
